import { useCallback } from 'react'
import { Loader2, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useClearProductParts } from '@/shared/hooks/api/catalog'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'

type V2ClearProductPartsDialogProps = {
  productId: string
  partCount: number
  open: boolean
  onOpenChange: (open: boolean) => void
  onCleared?: () => void
}

export function V2ClearProductPartsDialog({
  productId,
  partCount,
  open,
  onOpenChange,
  onCleared,
}: V2ClearProductPartsDialogProps) {
  const { t } = useTranslation('common')
  const { mutateAsync: clearParts, isPending } = useClearProductParts(productId)

  const handleClear = useCallback(async () => {
    try {
      const result = await clearParts()
      toast.success(
        t('v2.pages.products.detail.parts.clear.success', {
          count: result.removed,
        })
      )
      onOpenChange(false)
      onCleared?.()
    } catch {
      toast.error(t('v2.pages.products.detail.parts.clear.errors.failed'))
    }
  }, [clearParts, onCleared, onOpenChange, t])

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('v2.pages.products.detail.parts.clear.confirmTitle')}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t('v2.pages.products.detail.parts.clear.confirmDescription', {
              count: partCount,
            })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>
            {t('actions.cancel')}
          </AlertDialogCancel>
          <Button
            type="button"
            variant="destructive"
            disabled={isPending || partCount === 0}
            className="gap-2"
            onClick={() => {
              void handleClear()
            }}
          >
            {isPending ? (
              <>
                <Loader2 className="size-4 animate-spin" aria-hidden />
                {t('v2.pages.products.detail.parts.clear.loading')}
              </>
            ) : (
              <>
                <Trash2 className="size-4" aria-hidden />
                {t('v2.pages.products.detail.parts.clear.confirmButton')}
              </>
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
